import { useActiveSeason } from '@/store/active-season';
import { provide, createApp as createAppVue, h, inject } from 'vue';
import { definePiniaStores, listenAllGlobalStores, loadAllGlobalStores } from './store';
import Frame from '@pages/frame/index.vue';
import NoPlmAccess from '@pages/no-plm-access.vue';
import VueGtag from 'vue-gtag';
import { VueClipboard } from '@soerenmartius/vue3-clipboard';
import MasonryWall from '@yeger/vue-masonry-wall';
import { createPinia } from 'pinia';
import { createRouter, createWebHistory } from 'vue-router';
import { routes } from './router/routes';
import { useUser } from '@/store/user';
import { useUserConfig } from '@/store/user-config';
import { apiClient } from '@lib/http-client';
import { connectSignalr, onSignalrReconnected } from '@lib/signalr';
import * as selectedBrandStorage from '@lib/selected-brand-storage';
import { preloadRouteChunks } from '@lib/preload-routes';
import { vPrefetch } from '@lib/directives/prefetch';
import { openArticleDirective } from '@lib/directives/open-article';
import { installOverlayHistory } from '@lib/overlay-navigation';
import { readSnapshots, purgeBrand, type Snapshot } from '@lib/snapshot-cache';
import { registerPersistence, seedStores } from '@/store/warm-boot';
import dtoHashes from '@/lib/dto-hashes.json';
import { dismissBootScreen } from '@lib/boot-screen';

/** An error known before the app has mounted, shown by the frame as soon as it is. */
export type BootError = { title: string; message: string; fixed: boolean };

// The PLM app is served on multiple domains (e.g. app.resourced.com, qa.findsourcing.com).
// Only findsourcing.com hosts have the marketing/admin site that non-PLM users belong on,
// so only there is it safe to redirect them away.
function isFindsourcingHost(host: string) {
  return !!host && host.includes('findsourcing.com');
}

// export a factory function for creating fresh app, router and store
// instances
export async function createApp({ bootError, url, host }: { bootError?: BootError; url: string; host: string }) {
  const $api = apiClient;

  // Set when a logged-in user has no PLM brand but we can't safely redirect them to
  // the marketing/admin site (because we're not on a findsourcing.com domain). Boots
  // a minimal logout screen instead of the normal app — see the render() below.
  let noPlmAccess = false;

  // Pinia for global stores
  const pinia = createPinia();
  pinia.use(() => ({ $api }));

  // Warm boot: IndexedDB snapshots read after the auth check (keyed per user+brand),
  // seeded into the stores at mount so the app renders without waiting on the network.
  let snapshots: Record<string, Snapshot> = {};
  // True from seeding until the background revalidation settles. An error escaping in
  // that window is treated as possible snapshot corruption — see purgeAndReloadOnce.
  let warmBootWindow = false;
  const warmBootPurgedFlag = 'plm-warm-boot-purged';

  function purgeAndReloadOnce() {
    try {
      if (sessionStorage.getItem(warmBootPurgedFlag)) return; // already tried — let the error surface normally
      sessionStorage.setItem(warmBootPurgedFlag, '1');
    } catch {
      return;
    }
    const user = useUser(pinia);
    void purgeBrand(user.current!.id, user.brandId).finally(() => window.location.reload());
  }

  function endWarmBootWindow(revalidationSucceeded: boolean) {
    warmBootWindow = false;
    if (revalidationSucceeded) {
      try {
        sessionStorage.removeItem(warmBootPurgedFlag);
      } catch {
        /* ignore */
      }
    }
  }

  const app = createAppVue({
    beforeCreate() {
      this.$store = definePiniaStores(pinia);
      provide('$store', this.$store);
    },

    async beforeMount() {
      // In order to easily test the backend (and store layer), make the store and api always available from the console
      // Deliberately not declared on Window: app code reaches these through useStores() / useApi().
      Object.assign(window, { $store: this.$store, $api: this.$api });

      // Set user for Google Analytics. $gtag only exists when VITE_GA_ID gave us a property to
      // report to, so an environment without one has nothing to set.
      if (process.env.NODE_ENV !== 'development' && this.$gtag) {
        this.$gtag.set({
          userId: useUser().current!.userName,
        });
      }

      // Events broadcast while the connection was down are lost — reload everything
      // on reconnect so the caches can't drift silently (e.g. laptop waking from sleep).
      // Registered before the loggedIn check so a post-login connect (login page) gets it too.
      onSignalrReconnected(() => {
        loadAllGlobalStores(this.$store).catch((e) => console.error('Failed to reload stores after reconnect', e));
      });

      // Persist global store snapshots on every loadAll. Registered before the loggedIn
      // check so the post-login in-page load (login page) writes snapshots too.
      registerPersistence(this.$store);

      if (!useUser(pinia).loggedIn || noPlmAccess) {
        // No workspace to load, so nothing for the screen to cover: go now
        // rather than holding the floor in front of a login page.
        dismissBootScreen({ immediate: true });
        return;
      }

      const { unseeded, seededCount } = seedStores(this.$store, snapshots);

      if (seededCount === 0) {
        // Cold boot: everything loads in the foreground before the spinner goes away.
        try {
          // None of these depend on each other, except calculateActiveSeason which needs the
          // seasons store (and user.fullyLoaded) — so it chains on the store loads only.
          await Promise.all([
            connectSignalr(),
            loadAllGlobalStores(this.$store).then(() => this.$store.activeSeason.calculateActiveSeason(url)),
            this.$store.articleFilters.loadFilters(),
          ]);
          listenAllGlobalStores(this.$store);
          // Re-arm the crash safety net. The boot right after a purge is always cold, so
          // without this the purged flag would never clear and the one-shot safety net
          // would stay disabled for the rest of the tab's lifetime.
          endWarmBootWindow(true);
        } finally {
          dismissBootScreen();
        }
      } else {
        // Warm boot: stores are seeded from snapshots; only the few non-seedable stores
        // block rendering. Everything revalidates in the background right after.
        warmBootWindow = true;
        try {
          await Promise.all(unseeded.map((name) => this.$store[name].loadAll()));
          this.$store.user.fullyLoaded = true;
          this.$store.activeSeason.calculateActiveSeason(url);
        } finally {
          dismissBootScreen();
        }

        // The unseeded stores were just loaded from the network above — revalidating them
        // again here would refetch each one twice on every warm boot.
        Promise.all([
          connectSignalr(),
          loadAllGlobalStores(this.$store, unseeded),
          this.$store.articleFilters.loadFilters(),
        ])
          .then(() => {
            listenAllGlobalStores(this.$store);
            endWarmBootWindow(true);
          })
          .catch((e) => {
            endWarmBootWindow(false);
            console.error('Background revalidation failed', e);
          });
      }

      preloadRouteChunks();
    },

    render: function () {
      if (noPlmAccess) return h(NoPlmAccess);
      return h(Frame, { bootError });
    },
  });

  app.use(pinia);
  app.use({
    install(Vue, options = {}) {
      // Mixin adding this.$store to all components
      Vue.mixin({
        beforeCreate: function () {
          const store = inject('$store', undefined);
          if (store) this.$store = store;
        },
      });
    },
  });

  // Provide $api early so pinia stores that call useApi() in their setup can resolve
  // it when definePiniaStores runs further down.
  app.provide('$api', $api);

  // Optimistically fetch user-config in parallel with the auth check, using the brand id
  // remembered in storage (the common returning-user case — pre-auth, useUser().brandId
  // falls back to that same storage value). If the server resolves a different brand, or
  // the optimistic fetch failed, we fall back to a fresh load below.
  const storageBrandId = selectedBrandStorage.read();
  const optimisticConfig = storageBrandId
    ? useUserConfig(pinia)
        .ensureLoaded()
        .catch(() => null)
    : null;

  await useUser(pinia).ensureCheckedStatus();
  if (useUser(pinia).loggedIn) {
    // if user logged-in as non-plm brand, redirect to non-plm page
    if (!useUser(pinia).brandId) {
      // Only redirect to the findsourcing.com marketing/admin site when we're already
      // on a findsourcing.com domain. On other domains (e.g. app.resourced.com) that
      // external redirect strands the user on a different origin where they can't reach
      // the PLM logout — leaving them stuck as this user. There we boot a minimal logout
      // screen instead (rendered by the render() above).
      if (isFindsourcingHost(host)) {
        if (useUser(pinia).isAdmin) {
          throw {
            redirect: import.meta.env.DEV ? 'http://localhost:1338/admin' : 'https://www.findsourcing.com/admin',
          };
        } else {
          throw { redirect: import.meta.env.DEV ? 'http://localhost:1338/' : 'https://www.findsourcing.com' };
        }
      }

      noPlmAccess = true;
    }

    // User and userconfig is needed on all pages when logged in - so load early.
    if (!noPlmAccess) {
      const user = useUser(pinia);
      snapshots = await readSnapshots(user.current!.id, user.brandId);

      const configSnapshot = snapshots.userConfiguration;
      if (
        // Only usable when any in-flight optimistic fetch is for this same brand —
        // it then acts as the background revalidation of the seeded config.
        user.brandId === storageBrandId &&
        configSnapshot &&
        configSnapshot.schemaHash === dtoHashes.UserConfigSection
      ) {
        useUserConfig(pinia).seed(configSnapshot.data);
        if (optimisticConfig) {
          // Retry once in the background if the in-flight revalidation failed
          void optimisticConfig.then((result) => {
            if (result === null)
              useUserConfig(pinia)
                .load()
                .catch(() => {});
          });
        } else {
          useUserConfig(pinia)
            .load()
            .catch(() => {});
        }
      } else {
        // Await the optimistic fetch even when falling back — otherwise it could resolve
        // after the fresh load() and overwrite it with the wrong brand's config.
        const optimisticResult = optimisticConfig ? await optimisticConfig : null;
        if (optimisticResult === null || user.brandId !== storageBrandId) {
          await useUserConfig(pinia).load();
        }
      }
    }
  }

  // Router must be created after ensureLoaded() above, because routes() reads
  // useUserConfig().settings (e.g. latestView for the products redirect).
  // It must also be created before definePiniaStores (which happens at mount
  // via beforeCreate), because some stores call useRouter() in their setup.
  const router = createRouter({
    history: createWebHistory(),
    routes: routes(pinia),
    linkActiveClass: 'is-active',
  });
  app.use(router);

  // Before the season rewrite below, so that the overlay check compares the location that is
  // actually navigated to (season query and all) against the current one.
  installOverlayHistory(router);

  //#region Rewrite push/replace to work with active season
  const originalReplace = router.replace;
  const originalPush = router.push;

  router.replace = (to) => originalReplace(useActiveSeason(pinia).rewriteRouteLocation(to));
  router.push = (to) => originalPush(useActiveSeason(pinia).rewriteRouteLocation(to));
  //#endregion

  router.beforeEach((to, from) => {
    const userStore = useUser(pinia);

    const loginRouteName = 'login';
    if (!userStore.loggedIn) {
      if (to.meta?.noAuth) {
        return true;
      }

      return { name: loginRouteName, query: { redirect: encodeURIComponent(to.fullPath) } };
    }

    const mfaRouteName = 'account';
    if (userStore.need2fa) {
      if (to.name === mfaRouteName) {
        return true;
      }

      return { name: mfaRouteName };
    }
  });

  // Google Analytics — registered unconditionally until now, so an environment with no property
  // still loaded gtag and reported against an empty id.
  if (import.meta.env.VITE_GA_ID) {
    app.use(
      VueGtag,
      {
        appName: 'PLM',
        config: { id: import.meta.env.VITE_GA_ID },
      },
      router
    );
  }

  // Vue ClipBoard
  app.use(VueClipboard);

  // Masonry Grid
  app.use(MasonryWall);

  // Hover/focus data prefetch, e.g. v-prefetch="() => useProducts().prefetchProduct(id)"
  app.directive('prefetch', vPrefetch);

  // Marks the table row whose article is open in the details panel.
  app.directive('open-article', openArticleDirective(router));

  // Global properties, available on all components
  app.config.globalProperties.$api = $api;

  app.config.errorHandler = () => {
    // An error escaping while seeded-but-not-yet-revalidated may be caused by a bad
    // snapshot — purge this brand's snapshots and retry once as a cold boot.
    if (warmBootWindow) purgeAndReloadOnce();
  };

  return { app, router, pinia };
}
